<template>
  <aside
    class="ls-order"
    aria-label="Order"
  >
    <div class="ls-order__inner">
      <div
        v-if="$slots.chip"
        class="ls-order__chip"
      >
        <slot name="chip" />
      </div>
      <p
        class="ls-order__summary"
        :class="{ 'is-empty': !shortlistProducts.length }"
        aria-live="polite"
      >
        <template v-if="shortlistProducts.length">
          {{ shortlistProducts.length }} {{ shortlistProducts.length === 1 ? 'style' : 'styles' }} on your shortlist
        </template>
        <template v-else>
          Tap the heart on any style to shortlist it
        </template>
      </p>
      <div class="ls-order__actions">
        <button
          type="button"
          class="ls-order__pdf"
          data-test="ls-save-pdf"
          :disabled="printing"
          @click="$emit('print')"
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            aria-hidden="true"
            focusable="false"
          ><path
            d="M12 4v11m0 0-4-4m4 4 4-4M5 19h14"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          /></svg>
          <span>{{ printing ? 'Preparing PDF' : 'Save as PDF' }}</span>
        </button>
        <a
          v-if="href"
          class="ls-order__cta"
          data-test="ls-order-cta"
          :href="href"
          :target="cta.kind === 'website' ? '_blank' : null"
          :rel="cta.kind === 'website' ? 'noopener' : null"
          @click="$emit('order', cta.kind)"
        >{{ cta.label }}</a>
      </div>
    </div>
  </aside>
</template>

<script>
import { orderHref } from './orderLink'

export default {
  name: 'LsOrderBar',
  props: {
    cta: { type: Object, default: () => ({ kind: 'none' }) },
    shortlistProducts: { type: Array, default: () => [] },
    // KP-4572: styles marked interested and the sheet note go in the order email too.
    interestedProducts: { type: Array, default: () => [] },
    note: { type: String, default: '' },
    pageUrl: { type: String, default: '' },
    printing: { type: Boolean, default: false },
  },
  computed: {
    href() {
      return orderHref({
        cta: this.cta,
        products: this.shortlistProducts,
        interested: this.interestedProducts,
        note: this.note,
        pageUrl: this.pageUrl,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './ls-base';

// Sticky at the bottom of the viewport on phones while the collection scrolls
// under it, then settles into place at the end; inline on desktop.
.ls-order {
  position: sticky;
  bottom: 0;
  z-index: 25;
  margin-top: clamp(48px, 8vw, 96px);
  background: var(--ls-ground);
  border-top: 1px solid var(--ls-line);
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.06);
  padding-bottom: env(safe-area-inset-bottom, 0);

  @include ls-up($ls-lg) {
    position: static;
    box-shadow: none;
    border-bottom: 1px solid var(--ls-line);
  }

  &__inner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px 24px;
    max-width: $ls-max;
    margin: 0 auto;
    padding: 12px $ls-gutter;

    @include ls-up($ls-lg) {
      padding-top: 32px;
      padding-bottom: 32px;
    }
  }

  &__chip {
    display: flex;
    flex: 1 0 100%;
    min-width: 0;
    margin-bottom: -10px;

    @include ls-up($ls-md) {
      display: none;
    }
  }

  &__summary {
    margin: 0;
    color: var(--ls-muted);
    font: 400 0.875rem/1.4 var(--ls-body-font);

    // Phones keep the bar one row high: the hint is read out, not shown.
    &.is-empty {
      @media (max-width: #{$ls-lg - 1px}) {
        @include ls-sr-only;
      }
    }

    @include ls-up($ls-lg) {
      color: var(--ls-ink);
      font-size: 1rem;
    }
  }

  &__actions {
    display: flex;
    flex: 1 1 auto;
    justify-content: flex-end;
    gap: 10px;

    @include ls-up($ls-lg) {
      flex: none;
    }
  }

  %ls-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 48px;
    padding: 0 22px;
    border-radius: var(--ls-radius);
    font: 600 0.9375rem/1 var(--ls-body-font);
    text-decoration: none;
    cursor: pointer;
    touch-action: manipulation;
    transition: opacity 0.2s $ls-ease, background-color 0.2s $ls-ease;
    @include ls-focus;
  }

  &__cta {
    @extend %ls-button;
    flex: 1 1 auto;
    background: var(--ls-primary);
    color: var(--ls-on-primary);
    border: 1px solid var(--ls-primary);

    &:hover {
      opacity: 0.9;
      color: var(--ls-on-primary);
    }

    @include ls-up($ls-lg) {
      flex: none;
      min-width: 220px;
    }
  }

  &__pdf {
    @extend %ls-button;
    border: 1px solid var(--ls-line);
    background: transparent;
    color: var(--ls-ink);

    &:hover {
      border-color: var(--ls-ink);
    }

    &:disabled {
      cursor: progress;
      opacity: 0.6;
    }

    // On phones the PDF button is an icon with a hidden label, so the order
    // button keeps the width.
    span {
      @media (max-width: #{$ls-sm - 1px}) {
        @include ls-sr-only;
      }
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .ls-order__cta,
  .ls-order__pdf {
    transition: none;
  }
}
</style>
